import { useEffect, useState, useMemo } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { api } from '../api';

// ===== Types from API =====
interface ApiEvent {
  title: string;
  start: string;     // ISO string
  end?: string;      // ISO string (optional)
  allDay?: boolean;  // optional, may not be present for date-only items
}
interface BatchCalendar {
  batch_code: string;
  program_name: string;
  events: ApiEvent[];
}

// ===== UI event type =====
interface CalendarEvent {
  title: string;
  start: Date;
  end?: Date;
  allDay?: boolean;
  colorClass: string; // Tailwind class
}

type View = 'month' | 'week' | 'day' | 'agenda';

// ===== Color map (Tailwind) =====
const eventTypes = {
  'Launch': 'bg-blue-500',
  'Post-Assessment': 'bg-red-500',
  'Placement Drive': 'bg-green-500',
  'Pre-Assessment': 'bg-amber-500',
  'Proposal Approved': 'bg-purple-500',
  'Launchpad Session': 'bg-cyan-500',
  'Basecamp': 'bg-orange-500',
  'Domain Training': 'bg-emerald-500',
  'On-Job Training': 'bg-gray-500',
  'OJT': 'bg-gray-500', // handle "OJT (Day N)" titles
  'Session': 'bg-indigo-500',
};

const getEventColorClass = (title: string): string => {
  for (const key of Object.keys(eventTypes)) {
    if (title.includes(key)) return eventTypes[key as keyof typeof eventTypes];
  }
  return eventTypes['Session'];
};

// Treat these as all-day “date” events if API didn’t mark allDay
const ALLDAY_HINTS = [
  'Launchpad Session',
  'Basecamp',
  'Domain Training',
  'On-Job Training',
  'OJT',
  'Launch',
  'Placement Drive',
  'Proposal Approved',
];

// Add one day (ms)
const addOneDay = (d: Date) => new Date(d.getTime() + 24 * 60 * 60 * 1000);

// Coerce all-day when needed (react-big-calendar wants end on next day for 1-day allDay events)
const coerceAllDay = (ev: ApiEvent): { allDay: boolean; start: Date; end?: Date } => {
  const start = new Date(ev.start);
  const explicitAllDay = !!ev.allDay;
  const looksAllDay = ALLDAY_HINTS.some(h => ev.title.includes(h));
  const allDay = explicitAllDay || looksAllDay;

  if (allDay) {
    const end = ev.end ? new Date(ev.end) : addOneDay(start);
    return { allDay: true, start, end };
  }

  // timed event (pre/post/etc.)
  const end = ev.end ? new Date(ev.end) : undefined;
  return { allDay: false, start, end };
};

const localizer = momentLocalizer(moment);

const App = () => {
  const [allBatches, setAllBatches] = useState<BatchCalendar[]>([]);
  const [selectedBatchCode, setSelectedBatchCode] = useState<string | null>(null);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [view, setView] = useState<View>('month');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(false);
      try {
        // ✅ correct path
        const response = await api.get('/batches/api/batches/batch-events');
        const status = (response as any).status;
        if (status && status >= 400) throw new Error('Bad status');
        const fetched: BatchCalendar[] = (response as any).data;
        setAllBatches(fetched);
        if (fetched.length > 0) {
          setSelectedBatchCode(fetched[0].batch_code);
        }
      } catch (e) {
        console.error('Failed to load batches and events', e);
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const selectedBatchInfo = useMemo(
    () => allBatches.find(b => b.batch_code === selectedBatchCode) || null,
    [allBatches, selectedBatchCode]
  );

  const selectedBatchEvents: CalendarEvent[] = useMemo(() => {
    if (!selectedBatchInfo) return [];
    return selectedBatchInfo.events.map((e) => {
      const { allDay, start, end } = coerceAllDay(e);
      return {
        title: e.title,
        start,
        end,
        allDay,
        colorClass: getEventColorClass(e.title),
      };
    });
  }, [selectedBatchInfo]);

  const filteredEvents = useMemo(() => {
    if (!searchTerm) return selectedBatchEvents;
    const q = searchTerm.toLowerCase();
    return selectedBatchEvents.filter(ev => ev.title.toLowerCase().includes(q));
  }, [selectedBatchEvents, searchTerm]);

  // Find the closest relevant date: next upcoming event, otherwise the first event
  const recommendedDate = useMemo(() => {
    if (!selectedBatchEvents.length) return null;
    const now = new Date();
    const upcoming = selectedBatchEvents
      .map(ev => ev.start)
      .filter(d => d >= now)
      .sort((a, b) => a.getTime() - b.getTime())[0];
    if (upcoming) return upcoming;
    // fallback: earliest event in the batch
    return selectedBatchEvents
      .map(ev => ev.start)
      .sort((a, b) => a.getTime() - b.getTime())[0];
  }, [selectedBatchEvents]);

  useEffect(() => {
    if (recommendedDate) {
      setCurrentDate(recommendedDate);
    }
  }, [recommendedDate]);

  const jumpLabel = useMemo(() => {
    if (!recommendedDate) return 'No events';
    const now = new Date();
    return recommendedDate >= now ? 'Go to next event' : 'Go to first event';
  }, [recommendedDate]);

  const jumpTitle = useMemo(() => {
    if (!recommendedDate) return 'No events for this batch yet';
    return `Jump to ${recommendedDate.toLocaleDateString()}`;
  }, [recommendedDate]);

  // Tailwind class hook into RBC
  const eventPropGetter = (event: CalendarEvent) => {
    return { className: `rbc-event ${event.colorClass}` };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <p className="text-red-600">Error loading calendar.</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6 bg-gray-50 min-h-screen font-sans">
      {/* Header + Legend */}
      <div className="flex flex-col md:flex-row justify-between items-center flex-wrap gap-4 p-4 bg-white rounded-xl shadow-lg">
        <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">Batch Calendar</h1>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {Object.entries(eventTypes).map(([type, colorClass]) => (
            <div key={type} className="flex items-center space-x-2">
              <span className={`block w-4 h-4 rounded-full ${colorClass}`} />
              <span className="text-sm text-gray-700 font-medium">{type}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 p-4 bg-white rounded-xl shadow-lg">
        {/* Views */}
        <div className="flex flex-grow-0 gap-2 flex-wrap">
          {(['month','week','day','agenda'] as View[]).map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                view === v ? 'bg-indigo-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {v[0].toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>

        {/* Batch selector */}
        <div className="flex items-center space-x-2">
          <label htmlFor="batch-selector" className="text-sm font-semibold text-gray-700">Select Batch:</label>
          <select
            id="batch-selector"
            value={selectedBatchCode || ''}
            onChange={(e) => setSelectedBatchCode(e.target.value)}
            className="px-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 w-48 truncate"
          >
            {allBatches.map(batch => (
              <option key={batch.batch_code} value={batch.batch_code}>
                {batch.batch_code}
              </option>
            ))}
          </select>
        </div>

        {/* Selected batch info */}
        {selectedBatchInfo && (
          <div className="flex items-center space-x-2 text-sm text-gray-700 font-medium">
            <span>Program:</span>
            <span className="font-semibold text-gray-900">{selectedBatchInfo.program_name}</span>
          </div>
        )}

        {/* Search + Jump */}
        <div className="flex-grow flex items-center gap-3 w-full md:w-auto">
          <input
            type="text"
            placeholder="Search events..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            type="button"
            onClick={() => recommendedDate && setCurrentDate(recommendedDate)}
            disabled={!recommendedDate}
            className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition ${
              recommendedDate
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
            title={jumpTitle}
          >
            {jumpLabel}
          </button>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-white p-4 rounded-xl shadow-lg border border-gray-200" style={{ height: '80vh' }}>
        <Calendar
          localizer={localizer}
          events={filteredEvents}
          startAccessor="start"
          endAccessor="end"
          style={{ height: '100%' }}
          eventPropGetter={eventPropGetter}
          view={view}
          date={currentDate}
          onNavigate={(date) => setCurrentDate(date)}
          onView={(newView) => setView(newView as View)}
        />
        {!filteredEvents.length && (
          <div className="pt-6 text-center text-sm text-gray-500">No events to display.</div>
        )}
      </div>
    </div>
  );
};

export default App;
